"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.batchCreateCustomOrders = exports.createGridTradeOrdersV2 = exports.createGridTradeOrders = exports.verifyCanGridTrade = exports.useManualBatchTradeParams = exports.SPLIT_TRADE_MAX_COUNT = void 0;
const common_business_1 = require("@pionex-web-kit/common-business");
const decimal_js_1 = __importDefault(require("decimal.js"));
const react_1 = require("react");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const trade_utils_lib_1 = require("trade_utils_lib");
const FutureAPI_1 = require("../../../../apis/FutureAPI");
const interface_1 = require("../../../../interface");
const errors_1 = require("../../../../interface/errors");
const CustomAssetsProvider_1 = require("../../../Assets/CustomAssetsProvider");
const CalculateUtils_1 = require("../CalculateUtils");
const CheckFutureRequest_1 = require("../CheckFutureRequest");
const TradeValidate_1 = require("../TradeValidate");
const utils_1 = require("../utils");
const const_1 = require("./../../../../component/const");
const TextAndSliderSyncTool_1 = require("./TextAndSliderSyncTool");
const utils_2 = require("./utils");
/** 分仓买卖最大网格数量 */
exports.SPLIT_TRADE_MAX_COUNT = 20;
/** 合约手动网格下单(批量下单) */
function useManualBatchTradeParams(props) {
    var _a, _b;
    const { top, bottom, count, perAmount: _perAmount = "", investPercent = 0, actionType = "text", gridType, side, symbol, orderType, currentPrice, investSide, leverage, positionMode, reduceOnly, isFocused, totalCloseQty: _totalCloseQty = "", } = props;
    const amountPrecision = (_a = symbol.amount) !== null && _a !== void 0 ? _a : 2;
    const asset = (0, CustomAssetsProvider_1.useCustomAssets)(const_1.FutureAccountType, isFocused);
    const balance = (0, CustomAssetsProvider_1.useFuturesAvailable)(symbol.quote, isFocused);
    const allPositions = asset === null || asset === void 0 ? void 0 : asset.positionMap.get(symbol.symbol);
    const hadPositions = (0, react_1.useMemo)(() => { var _a, _b; return ((_b = (_a = asset === null || asset === void 0 ? void 0 : asset.positions) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0; }, [(_b = asset === null || asset === void 0 ? void 0 : asset.positions) === null || _b === void 0 ? void 0 : _b.length]);
    // asset 中的可用
    const _available = (0, react_1.useMemo)(() => {
        var _a, _b;
        return (new trade_utils_lib_1.SafeDecimal((_a = balance === null || balance === void 0 ? void 0 : balance.available) !== null && _a !== void 0 ? _a : "")
            // 有仓位时因为会有占用保证金的波动会导致可用不足，取0.98的冗余
            .mul(hadPositions ? 0.98 : 1)
            .toDP((_b = symbol.precisionCost) !== null && _b !== void 0 ? _b : 2, trade_utils_lib_1.SafeDecimal.ROUND_DOWN)
            .toString());
    }, [balance === null || balance === void 0 ? void 0 : balance.available, hadPositions, symbol.precisionCost]);
    // 下单时的仓位方向
    const positionSide = CalculateUtils_1.CalculateUtils.getPositionSide(positionMode, reduceOnly, side);
    // 获取最小下单量(投base)，或者获取最小下单额(投quote)
    // 由于分仓买卖都是限价单，直接采用限价单相关的函数来获取最小值
    const minPerAmount = (0, react_1.useMemo)(() => {
        return getLimitTradeMin(investSide, symbol, top, leverage, reduceOnly);
    }, [investSide, symbol, top, leverage, reduceOnly]);
    const { position, rPosition } = (0, react_1.useMemo)(() => utils_1.TradeUtils.getReversePosition(allPositions || [], positionSide, positionMode), [allPositions, positionMode, positionSide]);
    // 平仓仅支持按 base 平
    const maxCanClose = (0, react_1.useMemo)(() => {
        const positionQty = new trade_utils_lib_1.SafeDecimal(position === null || position === void 0 ? void 0 : position.positionQuantity);
        if (side === interface_1.CustomOrderSide.BUY) {
            if (positionQty.lessThan(0)) {
                return positionQty.abs().toString();
            }
        }
        else {
            if (positionQty.greaterThan(0)) {
                return positionQty.toString();
            }
        }
        return "";
    }, [position === null || position === void 0 ? void 0 : position.positionQuantity, side]);
    const supportReduceOnly = (0, react_1.useMemo)(() => (maxCanClose ? new trade_utils_lib_1.SafeDecimal(maxCanClose).gt(0) : false), [maxCanClose]);
    const [prices, sumPrice] = (0, react_1.useMemo)(() => {
        try {
            const priceArr = utils_2.GridUtils.getPriceInterverArr(top, bottom, count, gridType);
            const priceStrings = priceArr.map((e) => {
                var _a;
                return e.toDP((_a = symbol.precision) !== null && _a !== void 0 ? _a : 2, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR).toFixed();
            });
            // 平仓时直接忽略价格（认为每一格的价格都是1，这样总价格就是格子数），方便后续计算
            // 这个逻辑只有在基于base进行平仓时才正确，如果基于quote平仓则不能这样计算
            const sumPrice = priceStrings.reduce((total, current) => total.add(current), new trade_utils_lib_1.SafeDecimal(0)).toString();
            return [priceStrings, sumPrice];
        }
        catch (_a) {
            return [[], ""];
        }
    }, [top, bottom, count, gridType, symbol.precision]);
    const [bottomPrice, topPrice] = (0, react_1.useMemo)(() => (prices.length > 1 ? [prices[0], prices[prices.length - 1]] : []), [prices]);
    /**
     * 分仓买卖止盈止损挂在最容易成交的第一个单上，传close all
     *
     * 多仓，挂在区间上限价格单上
     *
     * 空仓，挂在区间下限价格单上
     */
    const tpSlOrderPrice = (0, react_1.useMemo)(() => (side === interface_1.CustomOrderSide.BUY ? topPrice : bottomPrice), [bottomPrice, side, topPrice]);
    // 获取最终的Available，该Available已经乘上了杠杆,canBuy表示每格最大可买量
    const [canBuy, canBuyDisplay, available] = (0, react_1.useMemo)(() => {
        var _a, _b;
        // 仅平仓模式下，只关注已有仓位上的量
        const availableD = reduceOnly
            ? new trade_utils_lib_1.SafeDecimal(position === null || position === void 0 ? void 0 : position.positionQuantity).abs()
            : new trade_utils_lib_1.SafeDecimal(_available).mul(new trade_utils_lib_1.SafeDecimal(leverage));
        if (sumPrice) {
            // 仅平仓，investSide 不为 base是，按格子数 count 计算每格可买量
            const canBuy = availableD
                .div(new trade_utils_lib_1.SafeDecimal(reduceOnly || investSide !== interface_1.InvestSymbolSide.base ? count : sumPrice))
                .toDP((_a = (investSide !== interface_1.InvestSymbolSide.base ? symbol.precisionCost : symbol.amount)) !== null && _a !== void 0 ? _a : 0, decimal_js_1.default.ROUND_DOWN)
                .toFixed();
            return [
                canBuy,
                canBuy,
                // 只有平仓时，availableD才是持仓 base 量，所以仅需要判断reduceOnly来取精度就可以了
                availableD.toDP((_b = (reduceOnly ? symbol.amount : symbol.precisionCost)) !== null && _b !== void 0 ? _b : 0, decimal_js_1.default.ROUND_DOWN).toString(),
            ];
        }
        return ["", "", availableD.toString()];
    }, [_available, count, investSide, leverage, position === null || position === void 0 ? void 0 : position.positionQuantity, reduceOnly, sumPrice, symbol.amount, symbol.precisionCost]);
    const [totalCloseQty, setTotalCloseQty] = (0, react_1.useState)(_totalCloseQty);
    (0, common_business_1.useUpdateEffect)(() => {
        setTotalCloseQty(_totalCloseQty);
    }, [_totalCloseQty]);
    const perAmount = (0, react_1.useMemo)(() => {
        if (reduceOnly && totalCloseQty && count) {
            const _count = new trade_utils_lib_1.SafeDecimal(count);
            if (_count.eq(0)) {
                return _perAmount;
            }
            const _total = new trade_utils_lib_1.SafeDecimal(totalCloseQty);
            return _total.div(_count).toDP(amountPrecision, trade_utils_lib_1.SafeDecimal.ROUND_DOWN).toFixed();
        }
        return _perAmount;
    }, [_perAmount, amountPrecision, count, reduceOnly, totalCloseQty]);
    const [parseAmount, parsePercent] = TextAndSliderSyncTool_1.TextAndSliderSyncTool.useSeparateTradingSync(actionType, perAmount, investPercent, count, sumPrice, available, symbol, reduceOnly, investSide, leverage);
    const positionQuantityAbsD = (0, react_1.useMemo)(() => {
        return new trade_utils_lib_1.SafeDecimal(position === null || position === void 0 ? void 0 : position.positionQuantity).abs();
    }, [position === null || position === void 0 ? void 0 : position.positionQuantity]);
    const totalCloseQtyPercent = (0, react_1.useMemo)(() => {
        return positionQuantityAbsD.gt(0) ? new trade_utils_lib_1.SafeDecimal(totalCloseQty).div(positionQuantityAbsD).toNumber() : 0;
    }, [positionQuantityAbsD, totalCloseQty]);
    const onTotalCloseQtyPercentChange = (0, common_business_1.usePersistFn)((value) => {
        const nQty = positionQuantityAbsD.mul(new trade_utils_lib_1.SafeDecimal(value)).toFixed(amountPrecision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR);
        setTotalCloseQty(nQty);
        return nQty;
    });
    const [baseInvests, sumBaseInvest] = (0, react_1.useMemo)(() => {
        let baseInvests = [];
        let sumBaseInvest = "";
        if (investSide === interface_1.InvestSymbolSide.base) {
            baseInvests = prices.map((a) => parseAmount);
            // 平仓时，将平仓分格后剩余的 base 添加到最后一格，防止平不完
            if (reduceOnly && totalCloseQty && count) {
                const lastInvest = new trade_utils_lib_1.SafeDecimal(totalCloseQty)
                    .sub(new trade_utils_lib_1.SafeDecimal(parseAmount).mul(new trade_utils_lib_1.SafeDecimal(count).sub(1)))
                    .toString();
                baseInvests[baseInvests.length - 1] = lastInvest;
            }
        }
        else {
            baseInvests = prices.map((price) => new trade_utils_lib_1.SafeDecimal(parseAmount).div(price).toString());
            if (investSide === interface_1.InvestSymbolSide.cost) {
                baseInvests = baseInvests.map((baseInvest) => new trade_utils_lib_1.SafeDecimal(baseInvest).mul(new trade_utils_lib_1.SafeDecimal(leverage)).toString());
            }
        }
        baseInvests = baseInvests.map((invest) => new trade_utils_lib_1.SafeDecimal(invest).toFixed(amountPrecision, trade_utils_lib_1.SafeDecimal.ROUND_FLOOR));
        sumBaseInvest = baseInvests.reduce((a, b) => a.plus(b), new trade_utils_lib_1.SafeDecimal(0)).toString();
        return [baseInvests, sumBaseInvest];
    }, [amountPrecision, count, investSide, leverage, parseAmount, prices, reduceOnly, totalCloseQty]);
    const [quoteInvests, sumQuoteInvest] = (0, react_1.useMemo)(() => {
        let quoteInvests = [];
        let sumQuoteInvest = "";
        if (investSide === interface_1.InvestSymbolSide.base) {
            quoteInvests = prices.map((price) => new trade_utils_lib_1.SafeDecimal(parseAmount).mul(price).toString());
            sumQuoteInvest = quoteInvests.reduce((a, b) => a.plus(b), new trade_utils_lib_1.SafeDecimal(0)).toString();
        }
        else {
            let quoteInvest = parseAmount;
            sumQuoteInvest = new trade_utils_lib_1.SafeDecimal(count).mul(new trade_utils_lib_1.SafeDecimal(parseAmount)).toString();
            if (investSide === interface_1.InvestSymbolSide.cost) {
                quoteInvest = new trade_utils_lib_1.SafeDecimal(parseAmount).mul(leverage).toString();
                sumQuoteInvest = new trade_utils_lib_1.SafeDecimal(sumQuoteInvest).mul(leverage).toString();
            }
            quoteInvests = prices.map((_) => quoteInvest);
        }
        return [quoteInvests, sumQuoteInvest];
    }, [count, investSide, leverage, parseAmount, prices]);
    const emptyErrors = (0, react_1.useMemo)(() => {
        const emptys = utils_2.GridUtils.checkRequiredParamsEmpty(top, bottom, count);
        // 优先判断上下限价格是否合法
        if (bottom && top && new trade_utils_lib_1.SafeDecimal(bottom).greaterThanOrEqualTo(new trade_utils_lib_1.SafeDecimal(top))) {
            emptys.push("gird_price_range_error");
        }
        // 平仓时，如果用户输入的平仓数量不够每格最小平仓量时报错
        if (reduceOnly && totalCloseQty && count) {
            if (!Number(parseAmount)) {
                emptys.push("futures_grid_close_min_base");
            }
        }
        else {
            if (!Number(parseAmount) && !_perAmount && new trade_utils_lib_1.SafeDecimal(investPercent).isZero()) {
                emptys.push("trade_grid_per_volume");
            }
        }
        const curPriceD = new trade_utils_lib_1.SafeDecimal(currentPrice);
        if (side === interface_1.CustomOrderSide.BUY) {
            // 做多
            if (new trade_utils_lib_1.SafeDecimal(bottom).gt(curPriceD)) {
                emptys.push("trade_grid_bottom_gt_cur_price");
            }
        }
        else {
            // 做空
            if (new trade_utils_lib_1.SafeDecimal(top).lt(curPriceD)) {
                emptys.push("trade_grid_top_lt_cur_price");
            }
        }
        return emptys;
    }, [top, bottom, count, reduceOnly, totalCloseQty, currentPrice, side, parseAmount, _perAmount, investPercent]);
    /** 是否会部分成交 */
    const willFilledPartial = (0, react_1.useMemo)(() => {
        if (reduceOnly) {
            return false;
        }
        const curPriceD = new trade_utils_lib_1.SafeDecimal(currentPrice);
        return new trade_utils_lib_1.SafeDecimal(top).gt(curPriceD) && new trade_utils_lib_1.SafeDecimal(bottom).lt(curPriceD);
    }, [bottom, currentPrice, reduceOnly, top]);
    const checkInvestState = (0, react_1.useCallback)(() => {
        const state = {
            state: "normal",
            min: minPerAmount + `(${investSide === interface_1.InvestSymbolSide.base ? symbol.baseDisplay : symbol.quoteDisplay})`,
            max: available + `(${side === interface_1.CustomOrderSide.BUY ? symbol.quote : symbol.baseDisplay})`,
        };
        if (parseAmount && sumQuoteInvest) {
            // 判断是否小于最小投资量
            if (new trade_utils_lib_1.SafeDecimal(parseAmount).lessThan(new trade_utils_lib_1.SafeDecimal(minPerAmount))) {
                state.state = "small";
                return state;
            }
            if (
            // 判断开单委托价值是否大于保证金以及最大可买量
            (!reduceOnly && new trade_utils_lib_1.SafeDecimal(sumQuoteInvest).greaterThan(new trade_utils_lib_1.SafeDecimal(available))) ||
                // 平仓时，判断平仓base总量是否大于仓位 base 量
                (reduceOnly && new trade_utils_lib_1.SafeDecimal(sumBaseInvest).greaterThan(new trade_utils_lib_1.SafeDecimal(position === null || position === void 0 ? void 0 : position.positionQuantity).abs()))) {
                state.state = "large";
                return state;
            }
        }
        return state;
    }, [
        available,
        investSide,
        minPerAmount,
        parseAmount,
        position === null || position === void 0 ? void 0 : position.positionQuantity,
        reduceOnly,
        side,
        sumBaseInvest,
        sumQuoteInvest,
        symbol.baseDisplay,
        symbol.quote,
        symbol.quoteDisplay,
    ]);
    const getBaseInvest = (tradePrice) => {
        var _a;
        if (investSide === interface_1.InvestSymbolSide.base) {
            return parseAmount;
        }
        else {
            let amount = new trade_utils_lib_1.SafeDecimal(parseAmount);
            if (investSide === interface_1.InvestSymbolSide.cost) {
                amount = amount.mul(leverage);
            }
            return new trade_utils_lib_1.SafeDecimal(amount)
                .div(new trade_utils_lib_1.SafeDecimal(tradePrice))
                .toDP((_a = symbol.amount) !== null && _a !== void 0 ? _a : 0, trade_utils_lib_1.SafeDecimal.ROUND_DOWN)
                .toFixed();
        }
    };
    // 开仓所需保证金
    const marginNeed = (0, react_1.useMemo)(() => {
        // 仅减仓时所需保证金为 0
        if (reduceOnly) {
            return "0";
        }
        if (!parseAmount || !sumPrice) {
            return "";
        }
        const leverageD = new trade_utils_lib_1.SafeDecimal(leverage);
        return new trade_utils_lib_1.SafeDecimal(sumQuoteInvest).div(leverageD).toString();
    }, [reduceOnly, parseAmount, sumPrice, leverage, sumQuoteInvest]);
    // 获取预估名义价值
    const getEsNotional = () => {
        // 获取当前的持仓价值（多仓的场景下不知道怎么去算持仓价值）
        const currentPositionValue = (() => {
            var _a, _b;
            const qty = (_a = position === null || position === void 0 ? void 0 : position.positionValue) !== null && _a !== void 0 ? _a : 0;
            if (positionMode === interface_1.OrderPositionMode.BUYSELL) {
                return new trade_utils_lib_1.SafeDecimal(qty);
            }
            const rQty = (_b = rPosition === null || rPosition === void 0 ? void 0 : rPosition.positionValue) !== null && _b !== void 0 ? _b : 0;
            return new trade_utils_lib_1.SafeDecimal(qty).abs().add(new trade_utils_lib_1.SafeDecimal(rQty).abs());
        })();
        // 获取当前的仓位符号
        const flag = (() => {
            if (positionMode === interface_1.OrderPositionMode.BUYSELL) {
                return side === interface_1.CustomOrderSide.BUY ? 1 : -1;
            }
            return reduceOnly ? -1 : 1;
        })();
        const newQuote = new trade_utils_lib_1.SafeDecimal(sumQuoteInvest).mul(flag);
        // 相加计算价值 向上取整
        return currentPositionValue
            .add(newQuote)
            .abs()
            .toDP(symbol.precision || 2, trade_utils_lib_1.SafeDecimal.ROUND_CEIL)
            .toFixed();
    };
    /** 平仓时，qty 是否大于仓位可平数量 */
    const isCloseQtyGtPositionQty = (0, react_1.useMemo)(() => {
        if (reduceOnly && supportReduceOnly) {
            let positionCanBuy = maxCanClose;
            if (new trade_utils_lib_1.SafeDecimal(totalCloseQty).abs().greaterThan(positionCanBuy)) {
                return true;
            }
        }
        return false;
    }, [maxCanClose, reduceOnly, supportReduceOnly, totalCloseQty]);
    return {
        orderType,
        side,
        top,
        bottom,
        count,
        gridType,
        perAmount: parseAmount,
        investPercent: parsePercent,
        currentPrice,
        available: (balance === null || balance === void 0 ? void 0 : balance.available) || "",
        symbol,
        minInvest: minPerAmount,
        limitPrices: prices,
        emptyErrors,
        checkInvestState,
        investSide,
        getBaseInvest,
        reduceOnly,
        canBuy,
        canBuyDisplay,
        leverage,
        positionMode,
        getEsNotional,
        marginNeed,
        willFilledPartial,
        topPrice,
        bottomPrice,
        tpSlOrderPrice,
        supportReduceOnly,
        maxCanClose,
        sumBaseInvest,
        sumQuoteInvest,
        baseInvests,
        quoteInvests,
        totalCloseQtyPercent,
        totalCloseQty,
        setTotalCloseQty,
        onTotalCloseQtyPercentChange,
        isCloseQtyGtPositionQty,
    };
}
exports.useManualBatchTradeParams = useManualBatchTradeParams;
/** 校验是否可以下合约网格下单 */
function verifyCanGridTradeImp(params, tradeParams) {
    const { markPrice = "", leverageConfigRow } = tradeParams;
    if (params.emptyErrors.length > 0) {
        return (0, rxjs_1.throwError)(errors_1.TRADE_MULTI_MISSING_PARAMS);
    }
    if (new trade_utils_lib_1.SafeDecimal(params.bottom).greaterThanOrEqualTo(new trade_utils_lib_1.SafeDecimal(params.top))) {
        return (0, rxjs_1.throwError)(errors_1.TRADE_MULTI_BOTTOM_GT_HIGHT);
    }
    if (params.limitPrices.length < 2 || params.limitPrices.length > exports.SPLIT_TRADE_MAX_COUNT) {
        return (0, rxjs_1.throwError)(Object.assign(Object.assign({}, errors_1.TRADE_MULTI_ROW_ERROR), { data: { min: 2, max: exports.SPLIT_TRADE_MAX_COUNT } }));
    }
    const investState = params.checkInvestState();
    if (investState.state === "small") {
        const errCode = params.investSide === interface_1.InvestSymbolSide.base ? errors_1.TRADE_MULTI_LT_MIN_INVEST_BASE : errors_1.TRADE_MULTI_LT_MIN_INVEST_QUOTE;
        return (0, rxjs_1.throwError)(Object.assign(Object.assign({}, errCode), { data: {
                amount: `${params.minInvest} ${params.investSide === interface_1.InvestSymbolSide.base ? params.symbol.baseDisplay : params.symbol.quoteDisplay}`,
            } }));
    }
    else if (investState.state === "large") {
        return (0, rxjs_1.throwError)(errors_1.TRADE_MULTI_GT_MAX_CAN_BUY);
    }
    const symbol = params.symbol;
    let availableD = new trade_utils_lib_1.SafeDecimal(params.available);
    // 非平仓下 判断交易后仓位价值是否会大于最大名义价值
    if (!params.reduceOnly) {
        const esNotional = params.getEsNotional();
        try {
            (0, CheckFutureRequest_1.verifyEsNotionalGtMaxEs)({
                esNotional: esNotional,
                notionalLimit: leverageConfigRow === null || leverageConfigRow === void 0 ? void 0 : leverageConfigRow.notional_limit,
                // 这里是原始的investSide
                investSide: params.investSide,
            });
        }
        catch (error) {
            return (0, rxjs_1.throwError)(error);
        }
    }
    const isBase = params.investSide === interface_1.InvestSymbolSide.base;
    const investSide = isBase ? interface_1.InvestSymbolSide.base : interface_1.InvestSymbolSide.quote;
    const invests = isBase ? params.baseInvests : params.quoteInvests;
    const lastInvest = invests[invests.length - 1];
    return (0, rxjs_1.zip)(...params.limitPrices.map((price, index) => {
        const amount = invests[index];
        return (0, CheckFutureRequest_1.verifyFuturesOrder)({
            accountType: trade_utils_lib_1.AccountTradeType.trader,
            symbol,
            orderType: params.orderType,
            side: params.side,
            price,
            curPrice: params.currentPrice + "",
            markPrice,
            investSide,
            originInvestSide: params.investSide,
            amount: amount,
            baseInvest: params.getBaseInvest(price),
            reduceOnly: params.reduceOnly,
            // TODO 每计算完一个减少一个
            available: availableD.toString(),
            // 如果是平仓模式，使用最后一格的开单量作为canBuy
            canBuy: params.reduceOnly ? lastInvest : params.canBuy,
            riskState: params.riskState,
            leverageConfigRow,
            // esNotional,
            positionMode: params.positionMode,
            marginType: interface_1.FutureMarginType.CROSS,
            leverage: params.leverage,
        });
    }));
}
/**
 * 校验是否能分仓下单
 */
function verifyCanGridTrade(params, tradeParams) {
    return verifyCanGridTradeImp(params, tradeParams).pipe((0, operators_1.catchError)((error) => (0, rxjs_1.throwError)((0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(error))));
}
exports.verifyCanGridTrade = verifyCanGridTrade;
/** 执行分仓下单，前端发起多个接口请求，后续将要被废弃 */
function createGridTradeOrders(requests) {
    return (0, rxjs_1.zip)(...requests.map(FutureAPI_1.postCustomOrder)).pipe((0, operators_1.catchError)((error) => (0, rxjs_1.throwError)((0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(error))));
}
exports.createGridTradeOrders = createGridTradeOrders;
/** 使用后端批量下单接口，执行分仓下单 */
function createGridTradeOrdersV2(requests) {
    if (!requests.length) {
        return (0, rxjs_1.throwError)(null);
    }
    const symbol = requests[0].symbol;
    const orders = requests.map((request) => ({
        price: request.price,
        amount: request.amount,
        side: request.side,
        position_side: request.position_side,
    }));
    return (0, FutureAPI_1.batchCreateOrder)(trade_utils_lib_1.AccountTradeType.trader, {
        symbol,
        orders,
        confirmed_sensitive: true,
    }).pipe((0, operators_1.map)((resp) => resp.orders || []), (0, operators_1.catchError)((error) => (0, rxjs_1.throwError)((0, trade_utils_lib_1.getTradeDependencyInstance)().localizeError(error))));
}
exports.createGridTradeOrdersV2 = createGridTradeOrdersV2;
/** 批量下单 */
function batchCreateCustomOrders(requests, marginType) {
    if (marginType === interface_1.FutureMarginType.CROSS) {
        // 全仓直接走批量下单
        return createGridTradeOrdersV2(requests);
    }
    // 逐仓走前端下单
    return createGridTradeOrders(requests);
}
exports.batchCreateCustomOrders = batchCreateCustomOrders;
const getLimitTradeMin = (investSide, symbol, price, leverage, reduceOnly) => {
    var _a, _b;
    const [min] = TradeValidate_1.TradeValidator.getLimitQtyRange(symbol, price, reduceOnly);
    if (investSide === interface_1.InvestSymbolSide.base) {
        return min.toDP((_a = symbol.amount) !== null && _a !== void 0 ? _a : 0, trade_utils_lib_1.SafeDecimal.ROUND_CEIL).toFixed();
    }
    else {
        let minQuote = min.mul(new trade_utils_lib_1.SafeDecimal(price));
        if (investSide === interface_1.InvestSymbolSide.cost) {
            minQuote = minQuote.div(new trade_utils_lib_1.SafeDecimal(leverage));
        }
        return minQuote.toDP((_b = symbol.precisionCost) !== null && _b !== void 0 ? _b : 0, trade_utils_lib_1.SafeDecimal.ROUND_CEIL).toFixed();
    }
};
